import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, BookOpen, FileText, Monitor, ClipboardList, Library, FolderOpen,
  ArrowRight, ChevronDown, Sparkles, LayoutTemplate, Zap, Clock, Shield,
  Layers, Link2
} from 'lucide-react';

import heroDeskImg from '../assets/hero-desk.jpg';
import synchronizedMaterialsImg from '../assets/synchronized-materials.jpg';

const FAQS = [
  {
    q: "What standards are supported?",
    a: "TeachBud supports Common Core, NGSS, and NGSS-aligned state standards. You can also upload your own district curriculum documents, and our AI will automatically extract and align to your specific learning objectives."
  },
  {
    q: "What grade levels is this for?",
    a: "TeachBud is specifically optimized for K–8 classrooms. The language, formatting, and pedagogical approaches are tailored for elementary and middle school students."
  },
  {
    q: "Can I edit the generated materials?",
    a: "Absolutely. TeachBud gives you a strong first draft, but you always have full control. You can edit text, adjust formatting, and tweak questions before printing or presenting."
  },
  {
    q: "Does it work with my existing curriculum?",
    a: "Yes! Our Curriculum Hub allows you to upload your existing pacing guides, scope and sequence documents, or unit plans. TeachBud reads these documents to ensure your generated materials perfectly match what you are required to teach."
  },
  {
    q: "What happens after the free trial?",
    a: "After your 7-day free trial, you will be prompted to choose a plan. If you decide TeachBud isn't for you, you can cancel before the trial ends and you won't be charged. Your account will simply pause."
  },
  {
    q: "Can I cancel at any time?",
    a: "Yes, you can cancel your subscription at any time right from your settings page. You will continue to have access until the end of your current billing cycle."
  }
];

const TOOLS = [
  {
    title: "Lesson Planner",
    description: "Build a complete lesson plan with objectives, engaging hooks, differentiated activities, and required materials in minutes.",
    icon: BookOpen,
    color: "bg-blue-100 text-blue-600 ring-blue-500/20"
  },
  {
    title: "Activity Sheet Generator",
    description: "Design beautiful, grade-appropriate worksheets and exercises that perfectly match your lesson's vocabulary and concepts.",
    icon: FileText,
    color: "bg-violet-100 text-violet-600 ring-violet-500/20"
  },
  {
    title: "Presentation Generator",
    description: "Instantly produce polished, classroom-ready slide decks that visually support your lesson without you touching a single layout setting.",
    icon: Monitor,
    color: "bg-amber-100 text-amber-600 ring-amber-500/20"
  },
  {
    title: "Assessment Generator",
    description: "Create quizzes, exit tickets, and rubrics that actually measure the standards you taught today.",
    icon: ClipboardList,
    color: "bg-emerald-100 text-emerald-600 ring-emerald-500/20"
  },
  {
    title: "Curriculum Hub",
    description: "Upload your district's curriculum documents. Our AI extracts your specific standards and ensures everything you generate is perfectly aligned.",
    icon: Library,
    color: "bg-rose-100 text-rose-600 ring-rose-500/20"
  },
  {
    title: "My Resources",
    description: "Your organized digital filing cabinet. Every lesson, sheet, and slide deck you create is safely stored, fully editable, and easy to duplicate for next year.",
    icon: FolderOpen,
    color: "bg-cyan-100 text-cyan-600 ring-cyan-500/20"
  }
];

function AccordionItem({ q, a }: { q: string, a: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 py-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left focus:outline-none"
      >
        <span className="text-lg font-semibold text-slate-800">{q}</span>
        <ChevronDown
          className={`text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          size={20}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pt-4 text-slate-600 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function HomePage() {
  const [, navigate] = useLocation();
  const basePath = import.meta.env.BASE_URL?.replace(/\/$/, '') ?? '';
  const [scrolled, setScrolled] = useState(false);
  const [annualBilling, setAnnualBilling] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 font-sans selection:bg-primary/20 selection:text-primary">
      {/* Navigation */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b ${
          scrolled
            ? 'bg-white/95 backdrop-blur-xl border-slate-200 shadow-sm py-2'
            : 'bg-transparent border-transparent py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Brand lockup — artistic black × green gradient border frame */}
          <div
            className="rounded-2xl p-[3px] flex-shrink-0 transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, #059669 0%, #0f172a 38%, #059669 62%, #0f172a 100%)',
              boxShadow: scrolled
                ? '0 2px 8px rgba(5,150,105,0.25), 0 1px 3px rgba(0,0,0,0.3)'
                : '0 4px 16px rgba(5,150,105,0.30), 0 2px 6px rgba(0,0,0,0.35)',
            }}
          >
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-3 bg-white rounded-[13px] px-2 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label="TeachBud — scroll to top"
            >
              {/* Logo image — large enough that the mascot is a clear brand anchor */}
              <img
                src={`${basePath}/logo.png`}
                alt="TeachBud"
                className={`w-auto transition-all duration-300 ${
                  scrolled
                    ? 'h-16 sm:h-18 md:h-20'
                    : 'h-20 sm:h-24 md:h-24 lg:h-28'
                }`}
              />
              {/* Text lockup: wordmark + tagline rendered in HTML so it's always sharp.
                  Hidden at md (768px) because the full nav appears there and space is tight;
                  the logo PNG already shows "TeachBud" at that size. Re-shown at lg (1024px+). */}
              <span className="hidden min-[480px]:flex md:hidden lg:flex flex-col items-start leading-none gap-1.5 pr-1">
                <span
                  className={`font-extrabold tracking-tight text-slate-900 transition-all duration-300 leading-none whitespace-nowrap ${
                    scrolled ? 'text-lg sm:text-xl lg:text-2xl' : 'text-xl sm:text-xl md:text-lg lg:text-[1.85rem]'
                  }`}
                >
                  TeachBud
                </span>
                {/* Slogan: only shown at lg+ where there's horizontal room */}
                <span
                  className={`hidden lg:block font-semibold tracking-[0.15em] uppercase transition-all duration-300 leading-none whitespace-nowrap ${
                    scrolled
                      ? 'text-[9px] text-primary/60'
                      : 'text-[10px] xl:text-[11px] text-primary/75'
                  }`}
                >
                  Teach Better&nbsp;·&nbsp;Save Time&nbsp;·&nbsp;Inspire Learning
                </span>
              </span>
            </button>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo('features')} className="text-sm font-medium text-slate-600 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm">Features</button>
            <button onClick={() => scrollTo('how-it-works')} className="text-sm font-medium text-slate-600 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm">How It Works</button>
            <button onClick={() => scrollTo('pricing')} className="text-sm font-medium text-slate-600 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm">Pricing</button>
            <button onClick={() => scrollTo('faq')} className="text-sm font-medium text-slate-600 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm">FAQ</button>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/sign-in')}
              className="text-sm font-semibold text-slate-700 hover:text-primary transition-colors hidden sm:block px-3 py-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/sign-up')}
              className="bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5 rounded-full shadow-sm shadow-primary/20 transition-all hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden px-6">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-blue-200/30 blur-[120px] pointer-events-none" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-amber-200/20 blur-[120px] pointer-events-none" />
          
          <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl">
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-6">
                <Sparkles size={14} />
                <span>The smart way to prep</span>
              </motion.div>
              <motion.h1 variants={fadeInUp} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
                Plan Once. <br className="hidden sm:block" />
                <span className="text-primary">Teach Everything.</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
                Create a standards-aligned lesson, then instantly generate the matching Activity Sheet, Presentation, and Assessment—all synchronized and ready for class.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center gap-4">
                <button
                  onClick={() => navigate('/sign-up')}
                  className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white text-base font-semibold px-8 py-4 rounded-full shadow-lg shadow-primary/25 transition-all hover:-translate-y-1 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                >
                  Start Your Free Trial
                  <ArrowRight size={18} />
                </button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="relative lg:ml-auto w-full max-w-lg lg:max-w-none mx-auto"
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-amber-500/10 rounded-3xl transform rotate-3 scale-105 blur-lg"></div>
              <img
                src={heroDeskImg}
                alt="A beautifully organized teacher's desk bathed in soft morning light"
                className="relative z-10 w-full h-auto rounded-3xl shadow-2xl border border-white/50 object-cover aspect-[4/3] bg-slate-200"
              />
              {/* Floating badges */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-4 z-20"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Monday is ready.</p>
                  <p className="text-xs text-slate-500">4 resources generated</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* The Problem / Empathy */}
        <section className="py-20 bg-white px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Sunday afternoons should be yours.
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                You care deeply about your students, but building high-quality, aligned materials for every lesson takes hours you don't have. TeachBud was built to give you a massive head start, so you can focus on teaching, not formatting.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Core Workflow (How It Works) */}
        <section id="how-it-works" className="py-24 px-6 relative overflow-hidden bg-slate-50">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                The Synchronized Workflow
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-slate-600">
                Type your lesson idea once. TeachBud instantly branches it out into four complete, linked resources that share the same vocabulary, concepts, and standards.
              </motion.p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <img
                  src={synchronizedMaterialsImg}
                  alt="3D illustration of educational materials seamlessly connecting"
                  className="w-full h-auto rounded-3xl shadow-xl border border-slate-200/60 object-cover bg-slate-200"
                />
              </motion.div>

              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
                variants={staggerContainer}
                className="space-y-8"
              >
                <motion.div variants={fadeInUp} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg shadow-md shadow-primary/20">1</div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Input the Lesson</h3>
                    <p className="text-slate-600">Just tell us the topic, grade level, and standard—or upload your school or district curriculum. TeachBud aligns every lesson to your local curriculum while including any specific activities you want.</p>
                  </div>
                </motion.div>
                <motion.div variants={fadeInUp} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg shadow-md shadow-primary/20">2</div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Generate the Core</h3>
                    <p className="text-slate-600">TeachBud creates a robust Lesson Plan with hooks, direct instruction, and differentiated practice.</p>
                  </div>
                </motion.div>
                <motion.div variants={fadeInUp} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg shadow-md shadow-primary/20">3</div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Multiply Automatically</h3>
                    <p className="text-slate-600">With one click, generate a matching Activity Sheet, Presentation slide deck, and Assessment rubric. All formatted and perfectly aligned.</p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The TeachBud System — Synchronized Instructional Design */}
        <section className="py-24 bg-slate-950 text-white px-6 relative overflow-hidden">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-blue-500/8 blur-[140px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/3 w-[500px] h-[500px] rounded-full bg-amber-500/8 blur-[140px] pointer-events-none" />

          <div className="max-w-6xl mx-auto relative z-10">
            {/* Header */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center max-w-3xl mx-auto mb-20"
            >
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-400/25 text-blue-300 text-xs font-bold uppercase tracking-wider mb-6">
                <Layers size={14} />
                <span>The TeachBud Method</span>
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-6">
                One mind.<br />
                <span className="text-primary">Three synchronized formats.</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-slate-400 leading-relaxed">
                A screenplay has one script. The actors, the director, and the subtitles all work from the same source — not three different versions of the story. TeachBud works exactly the same way. One lesson is designed once, then rendered into three formats used simultaneously in your classroom.
              </motion.p>
            </motion.div>

            {/* The three synchronized resources */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {/* "Used simultaneously" label */}
              <motion.div variants={fadeInUp} className="flex items-center justify-center gap-2 mb-8">
                <div className="h-px flex-1 max-w-24 bg-gradient-to-r from-transparent to-primary/40" />
                <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary px-4">
                  <Link2 size={13} /> Teacher &amp; students — same room, same moment, same content
                </span>
                <div className="h-px flex-1 max-w-24 bg-gradient-to-l from-transparent to-primary/40" />
              </motion.div>

              {/* Three synchronized cards */}
              <motion.div variants={fadeInUp} className="grid md:grid-cols-3 gap-4 mb-4">
                {/* Lesson Plan */}
                <div className="bg-slate-800/70 border border-blue-500/30 rounded-2xl p-7 flex flex-col gap-4 hover:border-blue-400/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0">
                      <BookOpen size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-blue-400 font-bold uppercase tracking-wider">Source of Truth</p>
                      <h3 className="font-bold text-white text-lg">Lesson Plan</h3>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    The teacher's complete guide — objectives, direct instruction, worked examples, and every question in the sequence. This is the master document that everything else mirrors exactly.
                  </p>
                </div>

                {/* Activity Sheet */}
                <div className="bg-slate-800/70 border border-violet-500/30 rounded-2xl p-7 flex flex-col gap-4 hover:border-violet-400/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-violet-500/20 text-violet-400 flex items-center justify-center flex-shrink-0">
                      <FileText size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-violet-400 font-bold uppercase tracking-wider">Student Workspace</p>
                      <h3 className="font-bold text-white text-lg">Activity Sheet</h3>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    Students follow along and write directly on this in real time. The same problems, examples, and tasks as the Lesson Plan — in the exact same order, with the exact same wording.
                  </p>
                </div>

                {/* Presentation */}
                <div className="bg-slate-800/70 border border-amber-500/30 rounded-2xl p-7 flex flex-col gap-4 hover:border-amber-400/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center flex-shrink-0">
                      <Monitor size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-amber-400 font-bold uppercase tracking-wider">Electronic Whiteboard</p>
                      <h3 className="font-bold text-white text-lg">Presentation</h3>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    Displayed live on the classroom screen while students use their Activity Sheet. Every example and problem appears on the board and on their paper — simultaneously, slide by slide.
                  </p>
                </div>
              </motion.div>

              {/* Assessment — intentionally different */}
              <motion.div variants={fadeInUp} className="bg-slate-800/40 border border-emerald-500/25 rounded-2xl p-7 flex items-start gap-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ClipboardList size={22} />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <p className="text-xs text-emerald-400 font-bold uppercase tracking-wider">After the Lesson</p>
                    <span className="text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 px-2 py-0.5 rounded-full font-medium">Intentionally different</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Assessment</h3>
                  <p className="text-sm text-slate-400 leading-relaxed max-w-3xl">
                    The Assessment does not duplicate the Lesson Plan content. It measures whether students actually learned the skills that were taught. Questions are aligned to the same standards and objectives, but presented fresh — so you can see what students truly retained, not just what they copied down.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Features / Tools */}
        <section id="features" className="py-24 bg-white px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              className="text-center max-w-2xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Six powerful tools. One seamless platform.</h2>
              <p className="text-lg text-slate-600">Everything you need to run your classroom, organized exactly how you think.</p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {TOOLS.map((tool, i) => {
                const Icon = tool.icon;
                return (
                  <motion.div
                    key={i}
                    variants={fadeInUp}
                    className="p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 hover:-translate-y-1 group"
                  >
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ring-4 transition-transform group-hover:scale-110 ${tool.color}`}>
                      <Icon size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{tool.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{tool.description}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-24 bg-slate-900 text-white px-6">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-6">
                Simple, transparent pricing.
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
                Invest in your weekends. Try everything free for 7 days.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex items-center justify-center gap-4">
                <span className={`text-sm font-medium ${!annualBilling ? 'text-white' : 'text-slate-400'}`}>Monthly</span>
                <button
                  onClick={() => setAnnualBilling(!annualBilling)}
                  className="relative inline-flex h-7 w-14 items-center rounded-full bg-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${annualBilling ? 'translate-x-8' : 'translate-x-1'}`} />
                </button>
                <span className={`text-sm font-medium flex items-center gap-2 ${annualBilling ? 'text-white' : 'text-slate-400'}`}>
                  Annually <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs">Save 20%</span>
                </span>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
            >
              {/* Free Trial Card */}
              <motion.div variants={fadeInUp} className="bg-slate-800 rounded-3xl p-8 md:p-10 border border-slate-700 relative overflow-hidden flex flex-col">
                <div className="absolute top-0 right-0 p-6 opacity-5"><Clock size={120} /></div>
                <h3 className="text-2xl font-bold mb-2">7-Day Free Trial</h3>
                <p className="text-slate-400 mb-6 h-12">Experience the full power of TeachBud with zero commitment.</p>
                <div className="mb-8">
                  <span className="text-5xl font-extrabold">$0</span>
                </div>
                <ul className="space-y-4 mb-8 flex-1">
                  {[
                    "Unlimited resource generation",
                    "Access to all 6 tools",
                    "Curriculum Hub uploads",
                    "Export to PDF and PPTX",
                    "Cancel anytime, no questions asked"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle2 size={18} className="text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/sign-up')}
                  className="w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-4 rounded-xl transition-colors mt-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-800"
                >
                  Start Free Trial
                </button>
              </motion.div>

              {/* Pro Card */}
              <motion.div variants={fadeInUp} className="bg-gradient-to-b from-primary to-blue-700 rounded-3xl p-8 md:p-10 border border-blue-500 relative overflow-hidden shadow-2xl shadow-primary/20 flex flex-col">
                <div className="absolute top-0 right-0 p-6 opacity-10"><Zap size={120} /></div>
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-blue-300 via-white to-blue-300 opacity-50"></div>
                
                <h3 className="text-2xl font-bold mb-2">Pro Teacher</h3>
                <p className="text-blue-100 mb-6 h-12">Everything you need to reclaim your time all year long.</p>
                <div className="mb-8 flex items-baseline gap-2 relative">
                  <span className="text-5xl font-extrabold">${annualBilling ? '7.99' : '9.99'}</span>
                  <span className="text-blue-200">/mo</span>
                </div>
                <div className="h-6 mb-8 -mt-6">
                  {annualBilling && (
                    <p className="text-sm text-blue-200">Billed $95.88 annually</p>
                  )}
                </div>
                <ul className="space-y-4 mb-8 flex-1">
                  {[
                    "Everything in Free Trial",
                    "Unlimited saved resources",
                    "Advanced AI model access",
                    "Priority email support",
                    "Early access to new features"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-blue-50">
                      <CheckCircle2 size={18} className="text-white flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/sign-up')}
                  className="w-full bg-white text-primary hover:bg-blue-50 font-bold py-4 rounded-xl transition-colors shadow-lg mt-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-600"
                >
                  Get Started
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-24 bg-white px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-slate-600">Everything you need to know before you start.</p>
            </motion.div>

            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="space-y-2"
            >
              {FAQS.map((faq, i) => (
                <motion.div key={i} variants={fadeInUp}>
                  <AccordionItem q={faq.q} a={faq.a} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-24 bg-blue-50 px-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-slate-200/[0.04] bg-[bottom_1px_center]" style={{ backgroundImage: 'radial-gradient(circle at center, #2563eb 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.1 }}></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">
                Ready to take back your weekend?
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
                Join teachers who are building better lessons in a fraction of the time.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <button
                  onClick={() => navigate('/sign-up')}
                  className="bg-primary hover:bg-primary/90 text-white text-lg font-bold px-10 py-5 rounded-full shadow-xl shadow-primary/30 transition-all hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-50"
                >
                  Start Your Free Trial
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Gradient border frame — black × green (footer, muted) */}
          <div
            className="rounded-2xl p-[3px]"
            style={{
              background: 'linear-gradient(135deg, #059669 0%, #0f172a 38%, #059669 62%, #0f172a 100%)',
              boxShadow: '0 2px 8px rgba(5,150,105,0.18), 0 1px 3px rgba(0,0,0,0.22)',
              opacity: 0.75,
            }}
          >
            <div className="flex items-center gap-2 bg-white rounded-[13px] px-3 py-1.5">
              <img src={`${basePath}/logo.png`} alt="TeachBud" className="h-16 w-auto grayscale opacity-60" />
              <span className="text-base font-bold text-slate-400">TeachBud</span>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
            <button onClick={() => navigate('/privacy')} className="text-sm text-slate-500 hover:text-slate-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm">Privacy Policy</button>
            <button onClick={() => navigate('/terms')} className="text-sm text-slate-500 hover:text-slate-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm">Terms of Service</button>
            <a href="mailto:help@teachbud.app" className="text-sm text-slate-500 hover:text-slate-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm">help@teachbud.app</a>
          </div>

          <p className="text-sm text-slate-400 text-center md:text-left">
            © {new Date().getFullYear()} TeachBud. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
